<template>
  <div>
    <div class="card" id="printMe">
      <div class="card-header">
        <div class="row">
          <div class="col">
            <h5 class="card-title">Warenverbrauch {{ filterMonth ? monthLabel(filterMonth) : '' }}</h5>
          </div>
          <div class="col-md-auto text-right">
            <p-button type="success" @click="newVerbrauch" class="mt-0 hidePrint mr-1"><i class="bi bi-file-earmark-plus"></i> Verbrauch erfassen</p-button>
            <p-button type="default" v-print="'#printMe'" title="Liste drucken" class="mt-0 mr-1 hidePrint"><i class="bi bi-printer-fill" style="font-size: 16px;"></i></p-button>
            <p-button v-if="canManageWaren" type="default" @click="openWarenDialog" class="mt-0 hidePrint mr-1" title="Artikel verwalten"><i class="bi bi-box-seam" style="font-size: 16px;"></i></p-button>
            <p-button v-if="canManageWaren" type="default" @click="openCategoryDialog" class="mt-0 hidePrint mr-1" title="Kategorien verwalten"><i class="bi bi-tags" style="font-size: 16px;"></i></p-button>
          </div>
        </div>
        <div class="row mt-2 hidePrint">
          <div class="col-md-4">
            <el-select v-model="filterMonth" placeholder="Monat wählen">
              <el-option v-for="m in monthsSorted" :key="m" :label="monthLabel(m)" :value="m" />
            </el-select>
          </div>
        </div>
      </div>
      <div class="card-body row">
        <div class="col-sm-12">
          <el-table :data="aggregatedConsumptions" stripe empty-text="Es konnten keine Einträge gefunden werden.">
            <el-table-column label="Artikel" min-width="100">
              <template slot-scope="scope">
                {{ scope.row.bezeichnung }}
              </template>
            </el-table-column>
            <el-table-column label="Kategorie" min-width="110">
              <template slot-scope="scope">
                {{ scope.row.kategorie }}
              </template>
            </el-table-column>
            <el-table-column label="Einheit" min-width="90">
              <template slot-scope="scope">
                {{ scope.row.einheit }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="filterMonth"
              min-width="120"
              :label="monthLabel(filterMonth)"
            >
              <template slot-scope="scope">
                {{ formatQtyWithUnit(scope.row.monthly[filterMonth] || 0, scope.row.einheit) }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="filterMonth"
              label=""
              width="90"
              align="center"
              class-name="hidePrintCell"
              header-align="center"
            >
              <template slot-scope="scope">
                <p-button
                  v-if="(scope.row.monthly[filterMonth] || 0) > 0"
                  type="default"
                  size="sm"
                  class="mt-0 hidePrint"
                  title="Einträge anzeigen"
                  @click="openEntriesDialog(scope.row)"
                >
                  <i class="bi bi-list-ul"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <el-dialog :title="verbrauchDialogTitle" :visible.sync="newDialog" :close-on-click-modal="false">
      <el-form label-position="top" @keyup.enter.native="saveVerbrauch">
        <el-form-item label="Artikel">
          <el-select v-model="form.wareId" filterable placeholder="Artikel wählen" style="width: 100%" :disabled="!!form.key">
            <el-option v-for="w in selectableWarenList" :key="w.key" :label="w.bezeichnung" :value="w.key" />
          </el-select>
        </el-form-item>
        <el-form-item label="Menge">
          <el-input :value="formQtyStr" @input="onQtyInput" placeholder="z.B. 1,50" style="width: 100%">
            <template slot="append">{{ selectedUnit }}</template>
          </el-input>
        </el-form-item>
        <el-form-item label="Datum">
          <el-date-picker
            v-model="form.date"
            type="date"
            placeholder="Datum wählen"
            :picker-options="pickerOptions1"
            style="width: 100%"
            format="dd.MM.yyyy"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <p-button @click="newDialog = false">Abbrechen</p-button>
        <p-button type="primary" @click="saveVerbrauch">Speichern</p-button>
      </span>
    </el-dialog>

    <el-dialog :title="entriesDialogTitle" :visible.sync="entriesDialog" width="720px" :close-on-click-modal="false">
      <el-table :data="entriesForSelected" stripe empty-text="Es wurden keine Einträge gefunden." header-row-class-name="text-primary">
        <el-table-column label="Datum" min-width="140">
          <template slot-scope="scope">{{ formatDate(scope.row.date) }}</template>
        </el-table-column>
        <el-table-column label="Menge" min-width="140">
          <template slot-scope="scope">{{ formatQtyWithUnit(scope.row.qty, entriesDialogUnit) }}</template>
        </el-table-column>
        <el-table-column label="" width="160" align="center">
          <template slot-scope="scope">
            <p-button type="default" size="sm" class="mt-0 mr-1" title="Eintrag bearbeiten" @click="editVerbrauch(scope.row)"><i class="bi bi-pencil-square"></i></p-button>
            <p-button type="danger" size="sm" class="mt-0" title="Eintrag löschen" @click="deleteVerbrauch(scope.row)"><i class="bi bi-trash"></i></p-button>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <p-button @click="entriesDialog = false">Schließen</p-button>
      </span>
    </el-dialog>

    <el-dialog title="Artikel verwalten" :visible.sync="warenDialog" width="900px" :close-on-click-modal="false">
      <div class="row mb-3">
        <div class="col-sm-8">
          <p class="dialog-hint mb-0">Hier lassen sich Artikelstammdaten bearbeiten oder neue Artikel anlegen.</p>
        </div>
        <div class="col-sm-4 text-right">
          <p-button type="success" @click="openWareEditDialog()" class="mt-0"><i class="bi bi-plus-circle"></i> Neuer Artikel</p-button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-sm-12 text-right">
          <el-switch v-model="showArchivedWaren" active-text="Archivierte anzeigen"></el-switch>
        </div>
      </div>
      <el-table :data="filteredWarenList" stripe empty-text="Es konnten keine Artikel gefunden werden." header-row-class-name="text-primary">
        <el-table-column prop="bezeichnung" label="Bezeichnung" min-width="200"></el-table-column>
        <el-table-column prop="kategorie" label="Kategorie" min-width="140"></el-table-column>
        <el-table-column prop="einheit" label="Einheit" min-width="100"></el-table-column>
        <el-table-column label="Status" width="90" align="center">
          <template slot-scope="scope">
            {{ scope.row.archived ? 'Archiviert' : 'Aktiv' }}
          </template>
        </el-table-column>
        <el-table-column label="Verbrauch" width="115" align="center">
          <template slot-scope="scope">
            {{ consumptionCountByWare[scope.row.key] || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="" min-width="160" align="center">
          <template slot-scope="scope">
            <p-button type="default" size="sm" @click="openWareEditDialog(scope.row)" class="mt-0 mr-1" title="Artikel bearbeiten"><i class="bi bi-pencil-square"></i></p-button>
            <p-button v-if="!scope.row.archived" type="warning" size="sm" @click="archiveWare(scope.row)" class="mt-0 mr-1" title="Artikel archivieren"><i class="bi bi-archive"></i></p-button>
            <p-button v-else type="success" size="sm" @click="restoreWare(scope.row)" class="mt-0 mr-1" title="Artikel aktivieren"><i class="bi bi-arrow-counterclockwise"></i></p-button>
            <p-button type="danger" size="sm" @click="deleteWare(scope.row)" class="mt-0 mr-0" title="Artikel löschen"><i class="bi bi-trash"></i></p-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog :title="wareDialogTitle" :visible.sync="wareEditDialog" width="520px" :close-on-click-modal="false">
      <el-form label-position="top" @keyup.enter.native="saveWare">
        <el-form-item label="Bezeichnung">
          <el-input v-model="wareForm.bezeichnung" placeholder="Bezeichnung"></el-input>
        </el-form-item>
        <el-form-item label="Kategorie">
          <el-select
            v-model="wareForm.kategorie"
            filterable
            allow-create
            default-first-option
            clearable
            placeholder="Kategorie wählen oder eingeben"
            style="width: 100%"
          >
            <el-option v-for="category in categoryOptions" :key="category" :label="category" :value="category" />
          </el-select>
        </el-form-item>
        <el-form-item label="Einheit">
          <el-input v-model="wareForm.einheit" placeholder="z.B. Stück, kg, l"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <p-button @click="wareEditDialog = false">Abbrechen</p-button>
        <p-button type="primary" @click="saveWare" :disabled="savingWare">Speichern</p-button>
      </span>
    </el-dialog>

    <el-dialog title="Kategorien verwalten" :visible.sync="categoryDialog" width="720px" :close-on-click-modal="false">
      <p class="dialog-hint">Neue Kategorien können direkt beim Anlegen oder Bearbeiten eines Artikels vergeben werden.</p>
      <el-table :data="categoriesWithUsage" stripe empty-text="Es wurden keine Kategorien gefunden." header-row-class-name="text-primary">
        <el-table-column prop="name" label="Kategorie" min-width="180"></el-table-column>
        <el-table-column prop="count" label="Artikel" width="100" align="center"></el-table-column>
        <el-table-column label="" width="140" align="center">
          <template slot-scope="scope">
            <p-button type="default" size="sm" @click="openCategoryEditDialog(scope.row)" class="mt-0 mr-0" title="Kategorie umbenennen"><i class="bi bi-pencil-square"></i></p-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog title="Kategorie umbenennen" :visible.sync="categoryEditDialog" width="520px" :close-on-click-modal="false">
      <el-form label-position="top" @keyup.enter.native="saveCategory">
        <el-form-item label="Aktuelle Kategorie">
          <el-input :value="categoryForm.originalName" disabled></el-input>
        </el-form-item>
        <el-form-item label="Neue Bezeichnung">
          <el-input v-model="categoryForm.name" placeholder="Neue Kategorie"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <p-button @click="categoryEditDialog = false">Abbrechen</p-button>
        <p-button type="primary" @click="saveCategory" :disabled="savingCategory">Speichern</p-button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import Vue from "vue";
import { db, Timestamp } from "../../../../firebaseDb";
import { Table, TableColumn, Tooltip, Select, Option, Input, InputNumber, Collapse, CollapseItem, MessageBox, Dialog, Form, FormItem, DatePicker, Switch } from "element-ui";
import router from "../../../../main";
import moment from "moment-timezone";
import store from "../../../../store";
import Print from "vue-print-nb";

Vue.use(Table);
Vue.use(TableColumn);
Vue.use(Tooltip);
Vue.use(DatePicker);
Vue.use(Print);

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber,
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    MessageBox,
    [Dialog.name]: Dialog,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [DatePicker.name]: DatePicker,
    [Switch.name]: Switch,
  },

  data() {
    return {
      location: store.getters.userlocation,
      role: store.getters.userrole,
      warenList: [],
      consumptions: [],
      hideZero: true,
      filterMonth: null,
      newDialog: false,
      entriesDialog: false,
      entriesDialogWareId: null,
      warenDialog: false,
      showArchivedWaren: false,
      wareEditDialog: false,
      categoryDialog: false,
      categoryEditDialog: false,
      savingWare: false,
      savingCategory: false,
      form: {
        key: null,
        date: new Date(),
        wareId: null,
        qty: null,
      },
      wareForm: {
        key: null,
        bezeichnung: "",
        kategorie: "",
        einheit: "",
        archived: false,
      },
      categoryForm: {
        originalName: "",
        name: "",
      },
      formQtyStr: "",
      pickerOptions1: {
        firstDayOfWeek: 1,
        disabledDate(time) {
          return time.getTime() > Date.now();
        },
        shortcuts: [
          {
            text: "Heute",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime());
              picker.$emit("pick", date);
            },
          },
          {
            text: "Gestern",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() - 3600 * 1000 * 24);
              picker.$emit("pick", date);
            },
          },
          {
            text: "Vorgestern",
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() - 3600 * 1000 * 24 * 2);
              picker.$emit("pick", date);
            },
          },
        ],
      },
    };
  },
  created() {
    if (this.location !== "FRA") {
      MessageBox.alert("Diese Seite steht am gewählten Standort nicht zur Verfügung.", "Zugriff verweigert", {
        confirmButtonText: "OK",
        type: "error",
      }).then(() => {
        router.push("/ket/overview");
      });
      return;
    }

    moment.locale("de");
    this.filterMonth = this.monthKey(new Date());
    this.load();
    store.watch(
      (state) => state.userlocation,
      () => {
        this.location = store.getters.userlocation;
        this.load();
      }
    );
    store.watch(
      (state) => state.userrole,
      () => {
        this.role = store.getters.userrole;
      }
    );
  },
  methods: {
    load() {
      this.loadWaren();
      this.loadConsumptions();
    },
    loadWaren() {
      db.collection("waren").orderBy("Bezeichnung").get().then((snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          list.push(this.mapWareDoc(doc));
        });
        this.warenList = list;
      }).catch((err) => {
        console.error("Fehler beim Laden der Waren:", err);
        this.warenList = [];
      });
    },
    loadConsumptions() {
      const baseRef = db.collection("warenverbrauch");
      const query = this.location ? baseRef.where("locationID", "==", this.location) : baseRef;
      query.get().then((snapshot) => {
        const cons = [];
        snapshot.forEach((doc) => {
          const d = doc.data();
          cons.push({
            key: doc.id,
            wareId: d.WareId || d.wareId,
            qty: d.Qty !== undefined ? d.Qty : d.qty,
            date: d.Date && typeof d.Date.toDate === "function" ? d.Date.toDate() : d.date || null,
          });
        });
        this.consumptions = cons;
      }).catch((err) => {
        console.error("Fehler beim Laden des Verbrauchs:", err);
        this.consumptions = [];
      });
    },
    mapWareDoc(doc) {
      const d = doc.data();
      return {
        key: doc.id,
        bezeichnung: d.Bezeichnung !== undefined ? d.Bezeichnung : (d.name || "Unbenannt"),
        einheit: d.Einheit !== undefined ? d.Einheit : (d.unit || ""),
        kategorie: d.Kategorie !== undefined ? d.Kategorie : (d.kategorie || d.category || ""),
        archived: Boolean(d.Archived !== undefined ? d.Archived : (d.archived !== undefined ? d.archived : d.isArchived)),
      };
    },
    buildWarePayload(ware) {
      const bezeichnung = this.normalizeText(ware.bezeichnung);
      const einheit = this.normalizeText(ware.einheit);
      const kategorie = this.normalizeText(ware.kategorie);
      const archived = Boolean(ware.archived);
      return {
        Bezeichnung: bezeichnung,
        Einheit: einheit,
        Kategorie: kategorie,
        Archived: archived,
        name: bezeichnung,
        unit: einheit,
        category: kategorie,
        kategorie,
        archived,
        isArchived: archived,
      };
    },
    normalizeText(value) {
      return (value || "").toString().trim();
    },
    ensureManagePermission() {
      if (this.canManageWaren) {
        return true;
      }
      MessageBox.alert("Bearbeiten ist erst ab Rolle 2 möglich.", "Zugriff verweigert", {
        confirmButtonText: "OK",
        type: "error",
      });
      return false;
    },
    getConsumptionCount(wareId) {
      return this.consumptionCountByWare[wareId] || 0;
    },
    monthKey(date) {
      const dt = new Date(date);
      const y = dt.getFullYear();
      const m = (dt.getMonth() + 1).toString().padStart(2, "0");
      return `${y}-${m}`;
    },
    monthLabel(key) {
      const [y, m] = key.split("-").map(Number);
      const d = new Date(y, m - 1, 1);
      return moment(d).format("MMMM YYYY");
    },
    newVerbrauch() {
      this.form = {
        key: null,
        date: new Date(),
        wareId: null,
        qty: null,
      };
      this.formQtyStr = "";
      this.newDialog = true;
    },
    editVerbrauch(entry) {
      this.form = {
        key: entry.key,
        date: entry.date ? new Date(entry.date) : new Date(),
        wareId: entry.wareId,
        qty: entry.qty,
      };
      const num = Number(entry.qty);
      this.formQtyStr = isNaN(num)
        ? ""
        : num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.newDialog = true;
    },
    saveVerbrauch() {
      if (!this.form.date || this.form.qty === null || !this.form.wareId) {
        return;
      }
      const payload = {
        Date: Timestamp.fromDate(new Date(this.form.date)),
        WareId: this.form.wareId,
        Qty: Number(this.form.qty),
        locationID: this.location,
      };
      if (this.form.key) {
        const editingKey = this.form.key;
        db.collection("warenverbrauch").doc(editingKey).update(payload).then(() => {
          const idx = this.consumptions.findIndex((c) => c.key === editingKey);
          const updated = {
            key: editingKey,
            wareId: payload.WareId,
            qty: payload.Qty,
            date: payload.Date.toDate(),
          };
          if (idx === -1) {
            this.consumptions.push(updated);
          } else {
            this.consumptions.splice(idx, 1, updated);
          }
          this.newDialog = false;
        }).catch((err) => {
          console.error("Fehler beim Aktualisieren des Verbrauchs:", err);
          MessageBox.alert("Der Verbrauch konnte nicht aktualisiert werden.", "Fehler", {
            confirmButtonText: "OK",
            type: "error",
          });
        });
        return;
      }
      db.collection("warenverbrauch").add(payload).then((docRef) => {
        this.consumptions.unshift({
          key: docRef.id,
          wareId: payload.WareId,
          qty: payload.Qty,
          date: payload.Date.toDate(),
        });
        this.newDialog = false;
      }).catch((err) => {
        console.error("Fehler beim Speichern des Verbrauchs:", err);
      });
    },
    deleteVerbrauch(entry) {
      MessageBox.confirm(
        `Verbrauchseintrag vom ${this.formatDate(entry.date)} wirklich löschen?`,
        "Eintrag löschen",
        {
          confirmButtonText: "Löschen",
          cancelButtonText: "Abbrechen",
          type: "warning",
        }
      ).then(() => {
        return db.collection("warenverbrauch").doc(entry.key).delete().then(() => {
          this.consumptions = this.consumptions.filter((c) => c.key !== entry.key);
        });
      }).catch(() => {});
    },
    openEntriesDialog(row) {
      this.entriesDialogWareId = row.wareId;
      this.entriesDialog = true;
    },
    openWarenDialog() {
      if (!this.ensureManagePermission()) {
        return;
      }
      this.warenDialog = true;
    },
    openWareEditDialog(ware = null) {
      if (!this.ensureManagePermission()) {
        return;
      }
      this.wareForm = {
        key: ware ? ware.key : null,
        bezeichnung: ware ? ware.bezeichnung : "",
        kategorie: ware ? ware.kategorie : "",
        einheit: ware ? ware.einheit : "",
        archived: ware ? ware.archived : false,
      };
      this.wareEditDialog = true;
    },
    saveWare() {
      if (!this.ensureManagePermission()) {
        return;
      }
      const bezeichnung = this.normalizeText(this.wareForm.bezeichnung);
      const einheit = this.normalizeText(this.wareForm.einheit);
      if (!bezeichnung || !einheit) {
        MessageBox.alert("Bitte Bezeichnung und Einheit ausfüllen.", "Unvollständige Eingabe", {
          confirmButtonText: "OK",
          type: "warning",
        });
        return;
      }

      const payload = this.buildWarePayload(this.wareForm);
      const duplicate = this.warenList.find((ware) => {
        if (ware.key === this.wareForm.key) {
          return false;
        }
        return this.normalizeText(ware.bezeichnung).toLowerCase() === payload.Bezeichnung.toLowerCase();
      });
      if (duplicate) {
        MessageBox.alert("Eine Ware mit dieser Bezeichnung existiert bereits.", "Doppelte Ware", {
          confirmButtonText: "OK",
          type: "warning",
        });
        return;
      }

      const existingWare = this.warenList.find((ware) => ware.key === this.wareForm.key);
      const hasConsumptions = existingWare && this.getConsumptionCount(existingWare.key) > 0;
      if (existingWare && hasConsumptions && this.normalizeText(existingWare.einheit) !== payload.Einheit) {
        MessageBox.confirm(
          "Für diese Ware existieren bereits Verbrauchseinträge. Eine Änderung der Einheit beeinflusst die Darstellung vorhandener Verbräuche. Trotzdem speichern?",
          "Einheit ändern",
          {
            confirmButtonText: "Ja",
            cancelButtonText: "Abbrechen",
            type: "warning",
          }
        ).then(() => {
          this.persistWare(payload);
        }).catch(() => {});
        return;
      }

      this.persistWare(payload);
    },
    persistWare(payload) {

      this.savingWare = true;
      const request = this.wareForm.key
        ? db.collection("waren").doc(this.wareForm.key).update(payload).then(() => ({ id: this.wareForm.key }))
        : db.collection("waren").add(payload).then((docRef) => ({ id: docRef.id }));

      request.then(({ id }) => {
        this.syncWare({
          key: id,
          bezeichnung: payload.Bezeichnung,
          einheit: payload.Einheit,
          kategorie: payload.Kategorie,
          archived: Boolean(payload.Archived),
        });
        this.wareEditDialog = false;
      }).catch((err) => {
        console.error("Fehler beim Speichern der Ware:", err);
        MessageBox.alert("Die Ware konnte nicht gespeichert werden.", "Fehler", {
          confirmButtonText: "OK",
          type: "error",
        });
      }).finally(() => {
        this.savingWare = false;
      });
    },
    archiveWare(ware) {
      if (!this.ensureManagePermission()) {
        return;
      }
      MessageBox.confirm(
        `Die Ware \"${ware.bezeichnung}\" wird archiviert und kann nicht mehr für neue Verbräuche ausgewählt werden. Fortfahren?`,
        "Ware archivieren",
        {
          confirmButtonText: "Archivieren",
          cancelButtonText: "Abbrechen",
          type: "warning",
        }
      ).then(() => {
        const payload = this.buildWarePayload({ ...ware, archived: true });
        return db.collection("waren").doc(ware.key).update(payload).then(() => {
          this.syncWare({ ...ware, archived: true });
        });
      }).catch(() => {});
    },
    restoreWare(ware) {
      if (!this.ensureManagePermission()) {
        return;
      }
      const payload = this.buildWarePayload({ ...ware, archived: false });
      db.collection("waren").doc(ware.key).update(payload).then(() => {
        this.syncWare({ ...ware, archived: false });
      }).catch((err) => {
        console.error("Fehler beim Aktivieren der Ware:", err);
        MessageBox.alert("Die Ware konnte nicht aktiviert werden.", "Fehler", {
          confirmButtonText: "OK",
          type: "error",
        });
      });
    },
    deleteWare(ware) {
      if (!this.ensureManagePermission()) {
        return;
      }
      if (this.getConsumptionCount(ware.key) > 0) {
        MessageBox.alert("Für diese Ware existieren bereits Verbrauchseinträge. Löschen ist deshalb gesperrt. Bitte archiviere die Ware stattdessen.", "Löschen nicht möglich", {
          confirmButtonText: "OK",
          type: "warning",
        });
        return;
      }
      MessageBox.confirm(
        `Die Ware \"${ware.bezeichnung}\" wird dauerhaft gelöscht. Fortfahren?`,
        "Ware löschen",
        {
          confirmButtonText: "Löschen",
          cancelButtonText: "Abbrechen",
          type: "warning",
        }
      ).then(() => {
        return db.collection("waren").doc(ware.key).delete().then(() => {
          this.warenList = this.warenList.filter((entry) => entry.key !== ware.key);
          if (this.form.wareId === ware.key) {
            this.form.wareId = null;
          }
        });
      }).catch(() => {});
    },
    syncWare(ware) {
      const idx = this.warenList.findIndex((entry) => entry.key === ware.key);
      if (idx === -1) {
        this.warenList.push(ware);
        return;
      }
      this.warenList.splice(idx, 1, ware);
    },
    openCategoryDialog() {
      if (!this.ensureManagePermission()) {
        return;
      }
      this.categoryDialog = true;
    },
    openCategoryEditDialog(category) {
      if (!this.ensureManagePermission()) {
        return;
      }
      this.categoryForm = {
        originalName: category.name,
        name: category.name,
      };
      this.categoryEditDialog = true;
    },
    saveCategory() {
      if (!this.ensureManagePermission()) {
        return;
      }
      const originalName = this.normalizeText(this.categoryForm.originalName);
      const newName = this.normalizeText(this.categoryForm.name);
      if (!originalName || !newName) {
        MessageBox.alert("Bitte einen neuen Kategorienamen angeben.", "Unvollständige Eingabe", {
          confirmButtonText: "OK",
          type: "warning",
        });
        return;
      }
      if (originalName === newName) {
        this.categoryEditDialog = false;
        return;
      }

      const duplicate = this.categoryOptions.find((category) => category.toLowerCase() === newName.toLowerCase());
      if (duplicate) {
        MessageBox.alert("Diese Kategorie existiert bereits.", "Doppelte Kategorie", {
          confirmButtonText: "OK",
          type: "warning",
        });
        return;
      }

      const affectedWaren = this.warenList.filter((ware) => this.normalizeText(ware.kategorie) === originalName);
      if (!affectedWaren.length) {
        this.categoryEditDialog = false;
        return;
      }

      this.savingCategory = true;
      Promise.all(
        affectedWaren.map((ware) => db.collection("waren").doc(ware.key).update(this.buildWarePayload({
          bezeichnung: ware.bezeichnung,
          einheit: ware.einheit,
          kategorie: newName,
          archived: ware.archived,
        })))
      ).then(() => {
        this.warenList = this.warenList.map((ware) => {
          if (this.normalizeText(ware.kategorie) !== originalName) {
            return ware;
          }
          return {
            ...ware,
            kategorie: newName,
          };
        });
        this.categoryEditDialog = false;
      }).catch((err) => {
        console.error("Fehler beim Umbenennen der Kategorie:", err);
        MessageBox.alert("Die Kategorie konnte nicht umbenannt werden.", "Fehler", {
          confirmButtonText: "OK",
          type: "error",
        });
      }).finally(() => {
        this.savingCategory = false;
      });
    },
    formatDate(d) {
      const dateObj = d && typeof d.toDate === "function" ? d.toDate() : d;
      return dateObj ? moment(dateObj).format("DD.MM.YYYY") : "";
    },
    onQtyInput(val) {
      let s = (val || "").replace(/\./g, ",");
      s = s.replace(/[^\d,]/g, "");
      const parts = s.split(",");
      const head = parts[0];
      const tail = parts[1] !== undefined ? parts[1].slice(0, 2) : undefined;
      s = head + (tail !== undefined ? "," + tail : "");
      this.formQtyStr = s;
      const num = parseFloat(s.replace(",", "."));
      this.form.qty = isNaN(num) ? null : Number(num.toFixed(2));
    },
    selectedWare() {
      return this.warenList.find((w) => w.key === this.form.wareId) || null;
    },
    formatQtyWithUnit(val, unit) {
      if (val === null || val === undefined) return "";
      const n = Number(val);
      if (isNaN(n)) return "";
      return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (unit ? " " + unit : "");
    },
  },
  computed: {
    monthsSorted() {
      const set = new Set();
      this.consumptions.forEach((c) => {
        if (!c.date) return;
        set.add(this.monthKey(c.date));
      });
      const arr = Array.from(set);
      arr.sort((a, b) => {
        const ad = new Date(a + "-01");
        const bd = new Date(b + "-01");
        return bd - ad;
      });
      return arr;
    },
    aggregatedConsumptions() {
      const byWare = new Map();
      this.warenList.forEach((w) => {
        byWare.set(w.key, { wareId: w.key, bezeichnung: w.bezeichnung, einheit: w.einheit, kategorie: w.kategorie, monthly: {} });
      });
      this.consumptions.forEach((c) => {
        if (!c.wareId || c.qty === undefined || c.date === null) return;
        const row = byWare.get(c.wareId) || { wareId: c.wareId, bezeichnung: "", einheit: "", kategorie: "", monthly: {} };
        const mk = this.monthKey(c.date);
        const prev = row.monthly[mk] || 0;
        const qty = Number(c.qty);
        row.monthly[mk] = prev + (isNaN(qty) ? 0 : qty);
        byWare.set(c.wareId, row);
      });
      let rows = Array.from(byWare.values());
      if (this.hideZero && this.filterMonth) {
        rows = rows.filter((r) => (r.monthly[this.filterMonth] || 0) > 0);
      }
      return rows.sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));
    },
    selectedUnit() {
      const w = this.selectedWare();
      return w ? w.einheit : "";
    },
    canManageWaren() {
      return Number(this.role) >= 2;
    },
    activeWarenList() {
      return this.warenListSorted.filter((ware) => !ware.archived);
    },
    warenListSorted() {
      return [...this.warenList].sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));
    },
    filteredWarenList() {
      if (this.showArchivedWaren) {
        return this.warenListSorted;
      }
      return this.activeWarenList;
    },
    consumptionCountByWare() {
      return this.consumptions.reduce((acc, consumption) => {
        if (!consumption.wareId) {
          return acc;
        }
        acc[consumption.wareId] = (acc[consumption.wareId] || 0) + 1;
        return acc;
      }, {});
    },
    categoryOptions() {
      return this.categoriesWithUsage.map((category) => category.name);
    },
    categoriesWithUsage() {
      const counts = new Map();
      this.warenList.forEach((ware) => {
        const category = this.normalizeText(ware.kategorie);
        if (!category) {
          return;
        }
        counts.set(category, (counts.get(category) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    wareDialogTitle() {
      return this.wareForm.key ? "Artikel bearbeiten" : "Neuer Artikel";
    },
    verbrauchDialogTitle() {
      return this.form.key ? "Verbrauch bearbeiten" : "Verbrauch erfassen";
    },
    selectableWarenList() {
      if (!this.form.key) {
        return this.activeWarenList;
      }
      const current = this.warenList.find((w) => w.key === this.form.wareId);
      const list = [...this.activeWarenList];
      if (current && !list.some((w) => w.key === current.key)) {
        list.push(current);
        list.sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));
      }
      return list;
    },
    entriesForSelected() {
      if (!this.entriesDialogWareId || !this.filterMonth) {
        return [];
      }
      return this.consumptions
        .filter((c) => c.wareId === this.entriesDialogWareId && c.date && this.monthKey(c.date) === this.filterMonth)
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    entriesDialogWare() {
      if (!this.entriesDialogWareId) {
        return null;
      }
      return this.warenList.find((w) => w.key === this.entriesDialogWareId) || null;
    },
    entriesDialogUnit() {
      const ware = this.entriesDialogWare;
      return ware ? ware.einheit : "";
    },
    entriesDialogTitle() {
      const ware = this.entriesDialogWare;
      const name = ware ? ware.bezeichnung : "Verbrauch";
      const month = this.filterMonth ? ` – ${this.monthLabel(this.filterMonth)}` : "";
      return `Einträge: ${name}${month}`;
    },
  },
};
</script>
<style>
.el-form-item__label {
  font-weight: bold;
  line-height: normal !important;
}

.hidePrint {
  @media print {
    display: none;
  }
}

.dialog-hint {
  color: #7a7a7a;
}

@media print {
  @page {
    size: A4 landscape;
    margin: 20mm;
  }

  #printMe,
  #printMe .card-body,
  #printMe .col-sm-12 {
    width: 100% !important;
    overflow: visible !important;
  }

  #printMe .el-table {
    width: 100% !important;
    font-size: 12px;
    overflow: visible !important;
  }

  #printMe .el-table__header-wrapper,
  #printMe .el-table__body-wrapper {
    overflow: visible !important;
    width: 100% !important;
  }

  #printMe .el-table__header-wrapper {
    padding-right: 0 !important;
  }

  #printMe .el-table__header,
  #printMe .el-table__body {
    width: 100% !important;
    table-layout: fixed !important;
  }

  #printMe .el-table colgroup col {
    width: auto !important;
  }

  #printMe .el-table .gutter,
  #printMe .el-table colgroup col[name="gutter"] {
    display: none !important;
    width: 0 !important;
  }

  #printMe .el-table th,
  #printMe .el-table td {
    padding: 4px 6px !important;
  }

  #printMe .el-table .cell {
    word-break: break-word;
    white-space: normal;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
}
</style>
